import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  Icon,
  Intent,
  ProgressBar,
} from "@blueprintjs/core"
import { Files, PlusCircle } from "@phosphor-icons/react"
import axios from "axios"
import CustomFileInput from "components/Atoms/CustomFileInput"
import { useHost } from "global/host.global"
import useAuth from "hooks/useAuth"
import React, { useState } from "react"
import AppToaster from "utils/AppToaster"

type UploaderDialogProps = {
  refetch?: () => void
}

export default function UploaderDialog({ refetch }: UploaderDialogProps) {
  const { token } = useAuth()
  const { subdomain, host, protocol } = useHost()

  const [isOpen, setIsOpen] = useState(false)
  const [fileName, changeFileName] = useState("Select File...")
  const [errors, changeErrors] = useState<string[]>([])

  async function handleChange({ target: { files } }) {
    const currentFile = files && files[0]
    if (!currentFile)
      return AppToaster.show({
        message: "returned",
        intent: "danger",
        icon: "cross",
      })

    if (
      !currentFile.name.endsWith(".xlsx") &&
      !currentFile.name.endsWith(".xls")
    ) {
      AppToaster.show({
        message: "Please upload a valid excel file (.xlsx, .xls)",
        intent: "danger",
        icon: "cross",
      })
      return
    }

    changeErrors([])
    const link = `${protocol}://${host}/services/importer/prod_batch/`
    changeFileName(`${currentFile.name} - ${currentFile.lastModified}`)

    const form = new FormData()
    form.append("file", currentFile)

    try {
      const response = await axios.post(link, form, {
        headers: {
          Tenant: subdomain,
          Authorization: `Bearer ${token}`,
        },
        onUploadProgress: ({ total, loaded }) => {
          if (!total) return
          const progress = loaded / total
          const percentage = Math.floor(progress * 100)
          AppToaster.show({
            icon: "cloud-upload",
            message: (
              <>
                <small>{currentFile.name}</small>
                <ProgressBar
                  className="vt-file-upload-progress"
                  intent={percentage < 100 ? Intent.PRIMARY : Intent.SUCCESS}
                  value={percentage / 100}
                />
              </>
            ),
            timeout: percentage < 100 ? 0 : 3000,
          })
        },
      })

      if (response.status === 200) {
        AppToaster.show({
          message: "File Uploaded Successfully",
          intent: "success",
          icon: "tick-circle",
        })

        if (response.data?.error) {
          const serverErrors = Array.isArray(response.data.error)
            ? response.data.error
            : [String(response.data.error)]
          changeErrors(serverErrors)
        }

        // Reset file input after successful upload
        changeFileName("Select File...")
        setIsOpen(false)
        changeErrors([])
        if (refetch) refetch()
      }
    } catch (error: any) {
      let extractedErrors: string[] = []

      if (error?.response) {
        const { data } = error.response

        if (Array.isArray(data?.error)) {
          extractedErrors = data.error.map((e: any) => String(e))
        } else if (typeof data?.error === "object") {
          extractedErrors = Object.values(data.error).map((e: any) =>
            Array.isArray(e) ? e.join(", ") : String(e)
          )
        } else if (typeof data?.error === "string") {
          extractedErrors = [data.error]
        }
      }

      if (extractedErrors.length > 0) {
        changeErrors(extractedErrors)
      }
    }
  }

  return (
    <>
      {/* Trigger Button */}
      <Button
        className="flex items-center bg-[#293742] text-white rounded"
        icon={<PlusCircle color="white" size={18} />}
        text="Upload Batch QC"
        intent="primary"
        minimal
        onClick={() => setIsOpen(true)}
      />

      {/* Dialog */}
      <Dialog
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
          changeErrors([])
          changeFileName("Select File...")
        }}
        canEscapeKeyClose
        canOutsideClickClose
        className="bg-white max-w-4xl w-full"
        title={
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Files size={24} /> Upload Batch QC
            </div>
          </div>
        }
      >
        <DialogBody>
          <div className="container flex flex-col items-center">
            <div className="w-full flex justify-center items-center flex-col py-8 border-2 border-dotted border-gray-400">
              <Icon iconSize={80} icon="cloud-upload" />
              <h6 className="text-lg mt-4">
                {fileName === "Select File..." ? "Upload" : "Replace"} Excel
                File
              </h6>
              <CustomFileInput
                text={fileName}
                onInputChange={handleChange as any}
              />

              {/* Error Messages */}
              {errors.length > 0 && (
                <div className="mt-4 w-full text-center text-red-600 text-sm space-y-1">
                  {errors.map((err, i) => (
                    <div key={i}>⚠️ {err}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogBody>

        <DialogFooter
          actions={
            <Button
              onClick={() => {
                setIsOpen(false)
                changeErrors([])
                changeFileName("Select File...")
              }}
              intent="none"
            >
              Close
            </Button>
          }
        />
      </Dialog>
    </>
  )
}
